import { AppSettings } from "@/AppSettings";
import { useMemo } from "react";
import { ConfigurationType } from ".";
import { ConfigurationSourceDataEvent, ConfigurationSourceDataMap, Metadata, SourceDataEventStatus, SourceDataPriority, Trial } from "@/api/dtos";

const excludedConfigurationSourceDataEventCopyProperties: Partial<Record<keyof ConfigurationSourceDataEvent, boolean>> = {
    id: true,
    trialId: true,
    configurationSourceDataId: true,
    studyEventOid: true,
    linked: true,
    enteredBy: true,
    enteredDate: true,
    modifiedBy: true,
    modifiedDate: true
}

export const copyConfigurationSourceDataEventSettings = (
    source: ConfigurationSourceDataEvent,
    target: ConfigurationSourceDataEvent,

) => {
    for (let pName in source) {
        const propertyName = pName as keyof ConfigurationSourceDataEvent;

        if (!excludedConfigurationSourceDataEventCopyProperties[propertyName]) {
            (target[propertyName] as any) = source[propertyName] as any;
        }
    }

    return target;
};

export const createEventConfiguration = (
    metadata?: Metadata, 
    trialConfigurationEvents?: ConfigurationSourceDataMap['events'], 
    configurationEvents?: ConfigurationSourceDataMap['events'], 
    trial?: Trial, 
    isGlobal?: boolean,
    configurationSourceDataId?: number, 
    studyEventOid?: string
) => {
    if (!metadata || !metadata.events || !studyEventOid) {
        return undefined;
    }

    const eventMetadata = metadata.events?.[studyEventOid];

    if (!eventMetadata) {
        return undefined;
    }

    const eventConfiguration = configurationEvents?.[studyEventOid];

    const trialEventConfiguration = trialConfigurationEvents?.[studyEventOid];

    const dominantConfiguration = eventConfiguration?.linked === false ? eventConfiguration : trialEventConfiguration;

    const configurationLink = `/${trial?.oid}/configuration/source-data/${isGlobal ? 'trial' : configurationSourceDataId ? configurationSourceDataId : 'new'}/events/${eventMetadata?.studyEventOid}`;
    
    return {
        eventMetadata: eventMetadata,
        eventConfiguration: eventConfiguration,
        trialEventConfiguration: trialEventConfiguration,

        linked: eventConfiguration?.linked !== false,
        type: eventConfiguration?.linked === false ?
            ConfigurationType.Event :
            ConfigurationType.TrialEvent,

        configuration: dominantConfiguration,

        icon: AppSettings.icons.event,

        studyEventOid: eventMetadata?.studyEventOid,

        displayName: dominantConfiguration?.name ?? eventMetadata?.name,

        priority: dominantConfiguration?.priority,

        configurationLink: configurationLink,
        configurationFormsLink: `${configurationLink}/forms`
    };
}

const useEventConfiguration = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    trial?: Trial, 
    configurationSourceDataId?: number, 
    studyEventOid?: string
) => {
    return useMemo(() => {
        return createEventConfiguration(
            metadata, 
            trialConfiguration?.events, 
            configuration?.events, 
            trial, 
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id ?? configurationSourceDataId, 
            studyEventOid
        );
    }, [metadata, trialConfiguration?.events, configuration?.events, studyEventOid, trial, configurationSourceDataId]);
};

export default useEventConfiguration;